<template>
  <b-modal
      id="weather-modal"
      hide-header-close
      hide-footer
      modal-class="weather-modal"
      dialog-class="weather-modal__dialog"
      content-class="weather-modal__content"
      header-class="weather-modal__header"
      body-class="weather-modal__body"
  >
    <template #modal-title>
      <div class="weather-title">
        <span class="title-label">{{ headerLabel }}</span>
        <span class="title-meta">{{ headerMeta }}</span>
      </div>
    </template>

    <div class="modal-body-content">
      <div class="message-block">
        <p v-for="(line, index) in bodyLines" :key="`weather-body-${index}`" class="message-line">
          {{ line }}
        </p>
      </div>

      <div v-if="hasAlerts" class="alerts-block">
        <div
            v-for="alert in normalizedAlerts"
            :key="alert.id"
            :class="['weather-card', alert.severityClass]"
        >
          <div class="weather-card__header">
            <span class="weather-card__label">{{ alert.label }}</span>
            <span class="weather-card__meta">{{ alert.timestamp }}</span>
          </div>
          <div class="weather-card__message">
            <p v-for="(line, idx) in alert.messageLines" :key="`weather-msg-${alert.id}-${idx}`">
              {{ line }}
            </p>
          </div>
        </div>
      </div>
      <div v-else class="empty-state">
        <p>No weather warnings are active for this vessel.</p>
      </div>
    </div>

    <div class="modal-actions">
      <b-button
          variant="outline-secondary"
          size="sm"
          class="action-button"
          @click="handleClose"
      >
        Close
      </b-button>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    payload: {
      type: Object,
      default: null,
    },
  },
  computed: {
    hasAlerts() {
      return Array.isArray(this.payload?.alerts) && this.payload.alerts.length > 0
    },
    isNormalWindMode() {
      if (!this.hasAlerts) return false

      return this.payload.alerts.every(alert => {
        const normalizedColor = this.normalizeColor(alert.color)

        if (normalizedColor === 'red' || normalizedColor === 'yellow') {
          return false
        }

        const textBlob = this.collectAlertText(alert)
        return normalizedColor === 'green' || textBlob.includes('normal wind')
      })
    },
    dominantSeverity() {
      if (!this.hasAlerts) return 'info'
      if (this.isNormalWindMode) return 'normal'

      const colors = this.payload.alerts.map(alert => this.normalizeColor(alert.color))
      if (colors.includes('red')) return 'critical'
      if (colors.includes('yellow')) return 'warning'
      if (colors.includes('green')) return 'info'
      return 'info'
    },
    headerLabel() {
      switch (this.dominantSeverity) {
        case 'normal':
          return 'Weather conditions are normal'
        case 'critical':
          return 'Severe weather detected'
        case 'warning':
          return 'Weather risks nearby'
        default:
          return 'Weather information'
      }
    },
    headerMeta() {
      const shipLabel = this.payload?.shipName || 'Weather overview'
      const updatedAt = this.payload?.updatedAt
      if (updatedAt) {
        const date = new Date(updatedAt)
        if (!Number.isNaN(date.getTime())) {
          return `${shipLabel} · Updated ${date.toLocaleString()}`
        }
      }
      return shipLabel
    },
    bodyLines() {
      if (!this.hasAlerts) {
        return [
          'There are no weather events affecting this vessel at the moment.',
          'The monitoring service will notify you if conditions worsen.',
        ]
      }

      const total = this.payload.alerts.length
      const severeCount = this.payload.alerts.filter(alert => this.normalizeColor(alert.color) === 'red').length
      const warningCount = this.payload.alerts.filter(alert => this.normalizeColor(alert.color) === 'yellow').length

      const lines = []

      if (this.isNormalWindMode) {
        lines.push(`Detected ${total} weather condition${total === 1 ? '' : 's'} near the vessel.`)
        lines.push('Wind levels remain within normal parameters.')
        lines.push('Monitoring will continue and alert you if conditions change.')
        return lines
      }

      lines.push(`Detected ${total} weather alert${total === 1 ? '' : 's'} near the vessel.`)

      if (severeCount > 0) {
        lines.push(`${severeCount} alert${severeCount === 1 ? '' : 's'} require immediate attention.`)
      }

      if (warningCount > 0) {
        lines.push(`${warningCount} alert${warningCount === 1 ? '' : 's'} indicate adverse conditions within the next hours.`)
      }

      lines.push('Review the details below and adjust operations or routing as required.')
      return lines
    },
    normalizedAlerts() {
      if (!this.hasAlerts) return []

      return this.payload.alerts.map((alert, index) => {
        const messageLines = Array.isArray(alert.texts)
          ? alert.texts.map(line => line.trim()).filter(Boolean)
          : (alert.message ? alert.message.split(/\r?\n/).map(line => line.trim()).filter(Boolean) : [])

        const timestamp = this.formatTimestamp(alert.timestamp)
        const severityClass = this.mapSeverityClass(this.normalizeColor(alert.color))

        const fallbackLabel = this.isNormalWindMode
          ? (this.payload.alerts.length === 1 ? 'Weather condition' : `Weather condition ${index + 1}`)
          : `Weather alert ${index + 1}`

        let label = alert.title ? alert.title.trim() : fallbackLabel

        if (this.isNormalWindMode && /weather alert/i.test(label)) {
          label = this.payload.alerts.length === 1 ? 'Weather condition' : `Weather condition ${index + 1}`
        }

        if (!label) {
          label = fallbackLabel
        }

        return {
          id: alert.id || `weather-${index}`,
          label,
          messageLines: messageLines.length > 0 ? messageLines : ['No additional details provided.'],
          timestamp,
          severityClass,
        }
      })
    },
  },
  watch: {
    payload: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.$bvModal.show('weather-modal')
          })
        } else {
          this.$bvModal.hide('weather-modal')
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleClose() {
      this.$bvModal.hide('weather-modal')
      this.$emit('close')
    },
    collectAlertText(alert) {
      const parts = []
      if (Array.isArray(alert?.texts)) {
        parts.push(...alert.texts)
      }
      if (alert?.text) {
        parts.push(alert.text)
      }
      if (alert?.message) {
        parts.push(alert.message)
      }
      if (alert?.title) {
        parts.push(alert.title)
      }
      return parts.join(' ').toLowerCase()
    },
    normalizeColor(color) {
      if (!color) return 'nocl'
      const normalized = color.toString().toLowerCase()
      if (normalized.includes('red')) return 'red'
      if (normalized.includes('yellow')) return 'yellow'
      if (normalized.includes('green')) return 'green'
      return 'nocl'
    },
    mapSeverityClass(color) {
      switch (color) {
        case 'red':
          return 'weather-card--danger'
        case 'yellow':
          return 'weather-card--warning'
        case 'green':
          return 'weather-card--info'
        default:
          return 'weather-card--neutral'
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString()
    },
  },
}
</script>

<style scoped>
.weather-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.title-label {
  color: #1f2940;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.title-meta {
  color: #5b6480;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 4px 2px 8px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  color: #1f2940;
}

.message-block {
  background: linear-gradient(135deg, rgba(250, 251, 255, 0.95), rgba(234, 237, 255, 0.9));
  border: 1px solid rgba(209, 214, 233, 0.7);
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 14px 28px rgba(24, 32, 60, 0.12);
}

.message-line {
  margin: 0 0 10px;
  font-size: 0.87rem;
  font-weight: 500;
  line-height: 1.4;
  color: #1f2940;
}

.message-line:last-child {
  margin-bottom: 0;
}

.alerts-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.weather-card {
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(218, 222, 240, 0.6);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 24px rgba(24, 32, 60, 0.12);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.weather-card__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.weather-card__label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #394063;
}

.weather-card__meta {
  font-size: 0.72rem;
  color: #6b7290;
}

.weather-card__message {
  font-size: 0.86rem;
  line-height: 1.35;
  color: #1f2940;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.weather-card__message p {
  margin: 0;
}

.weather-card--danger {
  border-color: rgba(234, 69, 86, 0.35);
  background: rgba(255, 240, 243, 0.92);
  box-shadow: 0 18px 32px rgba(220, 70, 94, 0.15);
}

.weather-card--warning {
  border-color: rgba(255, 193, 58, 0.35);
  background: rgba(255, 248, 229, 0.92);
  box-shadow: 0 18px 32px rgba(221, 167, 66, 0.15);
}

.weather-card--info {
  border-color: rgba(64, 181, 142, 0.28);
  background: rgba(240, 252, 247, 0.94);
  box-shadow: 0 18px 32px rgba(64, 181, 142, 0.12);
}

.weather-card--neutral {
  border-color: rgba(198, 205, 230, 0.38);
  background: rgba(247, 249, 255, 0.94);
  box-shadow: 0 18px 32px rgba(117, 126, 160, 0.12);
}

.empty-state {
  padding: 16px 20px;
  border-radius: 14px;
  background: rgba(247, 249, 255, 0.95);
  border: 1px dashed rgba(186, 194, 224, 0.6);
  text-align: center;
  color: #5b6480;
  font-size: 0.85rem;
}

.modal-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.action-button {
  min-width: 120px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
}

.action-button.primary {
  background: linear-gradient(135deg, #4b5fdd, #5669f5);
  border-color: rgba(75, 95, 221, 0.2);
}

::v-deep .weather-modal__dialog {
  max-width: 640px;
}

::v-deep .weather-modal__content {
  border-radius: 18px;
  border: none;
  box-shadow: 0 24px 40px rgba(20, 30, 55, 0.18);
}

::v-deep .weather-modal__header {
  background: linear-gradient(135deg, #f4f6ff 0%, #e6e9ff 100%);
  border-bottom: none;
  padding: 18px 22px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: inset 0 -1px 0 rgba(98, 111, 149, 0.12);
}

::v-deep .weather-modal__body {
  padding: 20px 24px 24px;
}
</style>